/**
 * 쿼리 실행 모니터링 — 편집기에서 **지금 실행 중인 문장**이 실행계획의 어느 줄에 있는지 본다(2026-10-13).
 *
 * 결과 영역의 「중지」 옆 「모니터링」 이 이 창을 연다. 열면 곧바로 수집을 시작해 몇 초마다 스냅샷을 쌓고(문장이 끝나거나
 * 「수집 중지」 를 누를 때까지), 아래 눈금으로 시점을 옮겨 가며 본다. 스냅샷은 이 창에만 있다 — 닫으면 사라진다.
 *
 * 줄의 표시는 **소스별로 따로** 적는다(한 칸에 섞지 않는다 — 백엔드 database/execMonitor.js):
 *   · 긴 작업(V$SESSION_LONGOPS)  — 풀스캔·정렬의 진척률. 팩 없이 된다
 *   · SQL Monitor                 — 줄마다의 행 수·시작 횟수. **Tuning Pack** 이 있어야 해서 켤 때만 조회한다
 *   · ASH                         — 최근 표본이 찍힌 줄과 대기 이벤트. **Diagnostics Pack** 이 있어야 해서 켤 때만 조회한다
 * 팩을 켜지 않으면 줄 표시는 긴 작업뿐이라 비어 보일 수 있다 — 그 까닭을 화면이 말한다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiActivity, FiPlay, FiSquare } from 'react-icons/fi';
import AppModal from './common/AppModal';
import { formatApiError } from '../utils/formatters';
import { monoWideText } from '../utils/monoWide';
import { listenDrag } from '../utils/dragListen';
import { execMonitorSnapshot } from '../services/execMonitor';
import { planLineId, noteKeys, fmtCount, fmtBytes, fmtMetric, seriesOf, sparkPath, TOTAL_METRICS, MAX_SNAPSHOTS, splitPlanSections, lineColumnsText, lineStarted, columnWidths } from '../utils/execMonitor';
import './ExecMonitorModal.css';
import memoWindow from './common/memoWindow';

const INTERVALS = [3, 5, 10, 30];
const PREF_KEY = 'livesql_exec_monitor_prefs';

function loadPrefs() {
  try { const p = JSON.parse(localStorage.getItem(PREF_KEY) || '{}'); return { intervalSec: INTERVALS.includes(p.intervalSec) ? p.intervalSec : 5, monitor: p.monitor === true, ash: p.ash === true }; }
  catch (_e) { return { intervalSec: 5, monitor: false, ash: false }; }
}
function savePrefs(p) { try { localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch (_e) { /* 저장 못 해도 창은 그대로 돈다 */ } }

const SPARK_W = 200;
const SPARK_H = 34;

/** 지표 하나의 작은 선 그래프 — 지금 보는 스냅샷 자리에 세로 금을 긋는다 */
function Spark({ points, idx }) {
  const d = sparkPath(points, SPARK_W, SPARK_H);
  const n = points.length;
  const x = n <= 1 ? SPARK_W / 2 : 2 + (idx * (SPARK_W - 4)) / (n - 1);
  return (
    <svg className="emon-spark" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} preserveAspectRatio="none" aria-hidden="true">
      {d ? <path d={d} /> : null}
      {d && idx >= 0 ? <line x1={x} x2={x} y1={0} y2={SPARK_H} /> : null}
    </svg>
  );
}
Spark.propTypes = { points: PropTypes.array.isRequired, idx: PropTypes.number };

function ExecMonitorModal({ open, onClose, target }) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(loadPrefs);
  const [collecting, setCollecting] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [sel, setSel] = useState(null);                 // null = 최신을 따라간다
  const [planText, setPlanText] = useState('');
  const [error, setError] = useState(null);
  const [ended, setEnded] = useState(null);             // 'finished' | 'stopped' | 'unsupported' | 'idle'
  const timerRef = useRef(null);
  const aliveRef = useRef(false);
  const planRef = useRef('');
  const countRef = useRef(0);                           // 지금까지 쌓은 스냅샷 수(끝났을 때 「볼 문장이 없었다」 와 「끝났다」 를 가른다)
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const sessionId = target && target.sessionId;

  const stop = useCallback((reason) => {
    aliveRef.current = false;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setCollecting(false);
    if (reason) setEnded(reason);
  }, []);

  const tick = useCallback(async () => {
    if (!aliveRef.current || !sessionId) return;
    const p = prefsRef.current;
    try {
      const snap = await execMonitorSnapshot(sessionId, { monitor: p.monitor, ash: p.ash, withPlan: !planRef.current });
      if (!aliveRef.current) return;
      if (snap.planText && !planRef.current) { planRef.current = snap.planText; setPlanText(snap.planText); }
      if (snap.supported === false) { setSnapshots(prev => [...prev, snap].slice(-MAX_SNAPSHOTS)); stop('unsupported'); return; }
      // 실행 중이 아니다 — 끝났다(처음부터 그랬으면 볼 문장이 없는 것). 끝난 뒤에는 더 묻지 않는다
      if (!snap.running) { stop(countRef.current ? 'finished' : 'idle'); return; }
      countRef.current += 1;
      setSnapshots(prev => [...prev, snap].slice(-MAX_SNAPSHOTS));
      timerRef.current = setTimeout(tick, p.intervalSec * 1000);
    } catch (err) {
      if (!aliveRef.current) return;
      setError(formatApiError(err, t));
      stop('stopped');
    }
  }, [sessionId, stop, t]);

  const start = useCallback(() => {
    if (!sessionId || aliveRef.current) return;
    setError(null); setEnded(null);
    aliveRef.current = true;
    setCollecting(true);
    tick();
  }, [sessionId, tick]);

  // 열면 곧바로 수집한다. 대상(세션)이 바뀌면 처음부터 — 앞 문장의 스냅샷이 섞이면 안 된다
  useEffect(() => {
    if (!open || !sessionId) return undefined;
    setSnapshots([]); setSel(null); setPlanText(''); planRef.current = ''; countRef.current = 0; setError(null); setEnded(null);
    aliveRef.current = true;
    setCollecting(true);
    tick();
    return () => { aliveRef.current = false; if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId, target && target.nonce]);

  const setPref = (patch) => { const next = { ...prefs, ...patch }; setPrefs(next); savePrefs(next); };

  const idx = sel == null ? snapshots.length - 1 : Math.min(sel, snapshots.length - 1);
  const snap = idx >= 0 ? snapshots[idx] : null;
  const lines = (snap && snap.lines) || {};
  // 계획 글을 절로 나눈다 — 계획 표만 펼치고 SQL 문·Predicate·Hint·Note 는 접어 둔다(2026-10-26 사용자 요청)
  const sections = useMemo(() => splitPlanSections(planText), [planText]);
  // 줄별 실측 칸(SQL Monitor) — 어느 스냅샷이든 SQL Monitor 값이 있으면 표에 칸을 붙인다
  const showCols = useMemo(() => snapshots.some(sn => Object.values((sn && sn.lines) || {}).some(l => l && l.monitor)), [snapshots]);
  // 칸 폭 — 모든 스냅샷의 가장 긴 값에 맞춘다(천만 단위 A-Rows 에 줄이 밀리지 않게, 2026-11-04)
  const colWidths = useMemo(() => columnWidths(snapshots), [snapshots]);
  const activeCount = Object.values(lines).filter(l => l.active).length;
  const notes = snap ? noteKeys(snap, prefs) : [];
  const series = useMemo(() => seriesOf(snapshots), [snapshots]);

  const lineInfo = (l) => {
    if (!l) return null;
    const parts = [];
    if (l.longops && l.longops.pct != null) parts.push(t('common:emon.longops', { pct: l.longops.pct, remain: l.longops.remainingSec == null ? '-' : l.longops.remainingSec }));
    // SQL Monitor 값은 표의 칸으로 보인다(showCols) — 칸이 없을 때만 줄 끝 글로
    if (l.monitor && !showCols) parts.push(t('common:emon.monitorLine', { rows: fmtCount(l.monitor.outputRows), starts: fmtCount(l.monitor.starts) }) + (l.monitor.readBytes ? ` · ${fmtBytes(l.monitor.readBytes)}` : ''));
    if (l.ash && l.ash.length) parts.push(l.ash.slice(0, 2).map(a => `${a.event} ×${a.samples}`).join(', '));
    return parts.join(' · ');
  };

  return (
    <AppModal open={open} onClose={onClose} title={t('common:emon.title')} icon={<FiActivity size={14} />} className="emon-modal" windowed>
      <div className="emon-body">
        <div className="emon-bar">
          <span className={`emon-dot${collecting ? ' on' : ''}`} aria-hidden="true" />
          <span className="emon-state">{collecting ? t('common:emon.collecting', { sec: prefs.intervalSec }) : t(`common:emon.ended.${ended || 'stopped'}`)}</span>
          {collecting
            ? <button type="button" className="app-btn app-btn-danger" onClick={() => stop('stopped')}><FiSquare size={11} /> {t('common:emon.stop')}</button>
            : <button type="button" className="app-btn app-btn-primary" onClick={start} disabled={!sessionId}><FiPlay size={11} /> {t('common:emon.resume')}</button>}
          <label className="emon-opt">{t('common:emon.interval')}
            <select value={prefs.intervalSec} onChange={e => setPref({ intervalSec: Number(e.target.value) })}>
              {INTERVALS.map(s => <option key={s} value={s}>{t('common:emon.seconds', { n: s })}</option>)}
            </select>
          </label>
          {/* 라이선스 팩이 필요한 소스 — 기본 꺼짐. 켠 것만 서버가 조회한다 */}
          <label className="emon-opt" title={t('common:emon.monitorHint')}><input type="checkbox" checked={prefs.monitor} onChange={e => setPref({ monitor: e.target.checked })} /> {t('common:emon.useMonitor')}</label>
          <label className="emon-opt" title={t('common:emon.ashHint')}><input type="checkbox" checked={prefs.ash} onChange={e => setPref({ ash: e.target.checked })} /> {t('common:emon.useAsh')}</label>
        </div>

        {/* 수집 대상 쿼리 — 길 수 있어 **기본 접힘**(2026-10-26). 접힌 줄에는 앞부분만 */}
        {target && target.sql ? (
          <details className="emon-sqlBox">
            <summary><b>{t('common:emon.target')}</b> <span className="emon-sqlHead">{String(target.sql).replace(/\s+/g, ' ').slice(0, 160)}</span></summary>
            <pre className="emon-sqlFull">{monoWideText(String(target.sql))}</pre>
          </details>
        ) : null}
        {error ? <div className="emon-error" role="alert">{error}</div> : null}

        {snap && snap.session ? (
          <div className="emon-session">
            <span>{t('common:emon.snapAt', { time: new Date(snap.at).toLocaleTimeString(), n: idx + 1, total: snapshots.length })}</span>
            {snap.exec ? <span>SQL_ID {snap.exec.sqlId}</span> : null}
            <span>{snap.session.event ? t('common:emon.waiting', { event: snap.session.event }) : (snap.session.onCpu ? t('common:emon.onCpu') : snap.session.status)}</span>
            {snap.session.lastCallEt != null ? <span>{t('common:emon.elapsed', { sec: snap.session.lastCallEt })}</span> : null}
            {snap.stmt ? <span>{t('common:emon.stmt', { gets: fmtCount(snap.stmt.bufferGets), reads: fmtCount(snap.stmt.diskReads), cpu: snap.stmt.cpuSec == null ? '-' : snap.stmt.cpuSec })}</span> : null}
            <span className="emon-activeCount">{t('common:emon.activeLines', { count: activeCount })}</span>
          </div>
        ) : null}

        {/* 시점 막대 — 수집한 스냅샷마다 눈금, 지금 보는 것은 강조. 눈금(또는 막대)을 누르면 그 시점으로(2026-11-04 —
            따로 목록을 두지 않고 막대가 목록 노릇을 한다). 키보드는 ← → Home End */}
        {snapshots.length > 1 ? (
          <div className="emon-scrub">
            <div
              className="emon-track"
              role="slider"
              tabIndex={0}
              aria-label={t('common:emon.scrub')}
              aria-valuemin={1}
              aria-valuemax={snapshots.length}
              aria-valuenow={idx + 1}
              aria-valuetext={snap ? t('common:emon.tickTitle', { n: idx + 1, time: new Date(snap.at).toLocaleTimeString() }) : undefined}
              onKeyDown={(e) => {
                const last = snapshots.length - 1;
                let next = null;
                if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = Math.max(0, idx - 1);
                else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = Math.min(last, idx + 1);
                else if (e.key === 'Home') next = 0;
                else if (e.key === 'End') next = last;
                if (next == null) return;
                e.preventDefault();
                setSel(next);
              }}
              onMouseDown={(e) => {
                // 막대 아무 곳이나 누르면(끌면) 가장 가까운 눈금 — 화면 px 끼리의 비율이라 CSS zoom 과 무관하다.
                // 끌기는 누른 자리의 문서에 건다(분리 창 — utils/dragListen)
                if (e.button !== 0) return;
                const el = e.currentTarget;
                const n = snapshots.length;
                const pick = (x) => {
                  const r = el.getBoundingClientRect();
                  if (!r.width) return;
                  const ratio = Math.min(1, Math.max(0, (x - r.left) / r.width));
                  setSel(Math.round(ratio * (n - 1)));
                };
                el.focus();
                pick(e.clientX);
                listenDrag(e, ev => pick(ev.clientX));
                e.preventDefault();
              }}
            >
              <div className="emon-trackFill" style={{ width: `${(idx * 100) / (snapshots.length - 1)}%` }} />
              {snapshots.map((sn, i) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key -- 스냅샷은 쌓이는 순서가 곧 정체다
                  key={i}
                  className={`emon-tick${i === idx ? ' on' : ''}`}
                  style={{ left: `${(i * 100) / (snapshots.length - 1)}%` }}
                  title={t('common:emon.tickTitle', { n: i + 1, time: new Date(sn.at).toLocaleTimeString() })}
                />
              ))}
            </div>
            <button type="button" className="app-btn" disabled={sel == null} onClick={() => setSel(null)}>{t('common:emon.follow')}</button>
          </div>
        ) : null}

        {notes.length ? <ul className="emon-notes">{notes.map(n => <li key={n.key}>{t(n.key, n.params)}</li>)}</ul> : null}

        {/* 왼쪽: 실행계획(스크롤) · 오른쪽: 합계 그래프(스크롤되지 않는다 — 계획을 내려도 그래프는 그 자리) */}
        <div className="emon-main">
          <div className="emon-planCol">
          {planText ? (
            <div className="emon-planScroll">
              {sections.head.length ? (
                <details className="emon-sec">
                  <summary>{t('common:emon.sec.sql')}</summary>
                  <pre className="emon-secBody">{monoWideText(sections.head.join('\n'))}</pre>
                </details>
              ) : null}
              <pre className="emon-plan">
                {sections.table.map((line, i) => {
                  const id = planLineId(line);
                  const l = id == null ? null : lines[String(id)];
                  const info = lineInfo(l);
                  return (
                    // eslint-disable-next-line react/no-array-index-key -- 계획 글의 줄은 순서가 곧 정체다
                    <span key={i} className={`emon-line${l && l.active ? ' active' : ''}${l && !l.active && (lineStarted(l) || l.longops) ? ' touched' : ''}`}>
                      {/* 표시 칸은 늘 같은 폭 — 화살표가 붙는 줄만 글이 밀리지 않게(2026-10-26) */}
                      <span className="emon-mark" aria-hidden="true">{l && l.active ? '▶' : ''}</span>
                      {monoWideText(line)}
                      {showCols ? lineColumnsText(line, l && l.monitor, colWidths) : null}
                      {info ? <span className="emon-info">  {info}</span> : null}
                      {'\n'}
                    </span>
                  );
                })}
              </pre>
              <div className="emon-colNote">{t(showCols ? 'common:emon.colNoteOn' : 'common:emon.colNoteOff')}</div>
              {sections.tail.map((sec, i) => (
                // eslint-disable-next-line react/no-array-index-key -- 절의 순서가 곧 정체다
                <details key={i} className="emon-sec">
                  <summary>{sec.title || t('common:emon.sec.more')}</summary>
                  <pre className="emon-secBody">{monoWideText(sec.lines.join('\n'))}</pre>
                </details>
              ))}
            </div>
          ) : (!error && collecting ? <div className="emon-empty">{t('common:state.loading')}</div> : null)}
          </div>
          {snapshots.length ? (
            <aside className="emon-side" aria-label={t('common:emon.tot.title')}>
              <div className="emon-sideTitle">{t('common:emon.tot.title')}</div>
              {TOTAL_METRICS.map((m) => {
                const sr = series[m.id];
                const cur = idx >= 0 ? sr.points[idx] : null;
                return (
                  <div key={m.id} className="emon-metric" title={t(`common:emon.tot.basis.${sr.basis}`)}>
                    <div className="emon-metricHead">
                      <span className="emon-metricName">{t(`common:emon.tot.${m.id}`)}</span>
                      <span className="emon-metricVal">{fmtMetric(m.unit, cur)}{m.unit === 'sec' && cur != null ? t('common:emon.tot.secUnit') : ''}</span>
                    </div>
                    <Spark points={sr.points} idx={idx} />
                    <div className="emon-metricFoot">
                      <span>{t(`common:emon.tot.basisShort.${sr.basis}`)}</span>
                      <span>{sr.max == null ? t('common:emon.tot.none') : t('common:emon.tot.max', { value: fmtMetric(m.unit, sr.max) })}</span>
                    </div>
                  </div>
                );
              })}
            </aside>
          ) : null}
        </div>
      </div>
    </AppModal>
  );
}

ExecMonitorModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** { sessionId, sql, nonce } — 모니터링할 편집기 세션과 그 문장 */
  target: PropTypes.object,
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(ExecMonitorModal);
